import type { SignedPackage } from "./types";
import type { RefundQuote } from "./recovery-refund";
export interface RecoveryRecord {
  id: string;
  paymentAddress: string;
  savedAt: number;
  package: SignedPackage;
  refundAttempt?: { quote: RefundQuote; raw: string };
}
export function recoveryRecord(
  paymentAddress: string,
  value: SignedPackage,
): RecoveryRecord {
  if (
    !/^[0-9a-f]{64}$/i.test(value.commitTxid) ||
    !/^[0-9a-f]{64}$/i.test(value.revealTxid)
  )
    throw new Error("Invalid recovery transaction");
  // Completed signed transactions can be replayed without retaining ephemeral private keys.
  return {
    id: value.revealTxid,
    paymentAddress,
    savedAt: Date.now(),
    package: {
      commitTxid: value.commitTxid,
      revealTxid: value.revealTxid,
      commitHex: value.commitHex,
      revealHex: value.revealHex,
      commitAuthorization: value.commitAuthorization,
    },
  };
}
function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("stc-recovery", 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore("packages", { keyPath: "id" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(new Error("Recovery storage is unavailable"));
  });
}

/** Preserve the original package and save the exact signed refund before any submission. */
export async function saveRecoveryRefundAttempt(id: string, attempt: NonNullable<RecoveryRecord["refundAttempt"]>) {
  const db = await database();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction("packages", "readwrite");
      const store = tx.objectStore("packages");
      const request = store.get(id);
      request.onsuccess = () => {
        if (!request.result) { tx.abort(); return; }
        store.put({ ...request.result, refundAttempt: attempt });
      };
      tx.oncomplete = () => resolve();
      tx.onabort = tx.onerror = () => reject(new Error("Could not preserve the signed refund. Nothing was sent."));
    });
  } finally { db.close(); }
}
export async function saveRecoveryPackage(
  address: string,
  value: SignedPackage,
) {
  const db = await database();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction("packages", "readwrite");
      const store = tx.objectStore("packages");
      const next = recoveryRecord(address, value);
      const existing = store.get(next.id);
      existing.onsuccess = () => store.put({ ...existing.result, ...next });
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(new Error("Could not save the recovery package"));
    });
  } finally {
    db.close();
  }
}
export async function readRecoveryPackages(
  address: string,
): Promise<RecoveryRecord[]> {
  const db = await database();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction("packages", "readonly");
      const request = tx.objectStore("packages").getAll();
      request.onsuccess = () =>
        resolve(
          (request.result as RecoveryRecord[])
            .filter(
              (r) => r.paymentAddress.toLowerCase() === address.toLowerCase(),
            )
            .sort((a, b) => b.savedAt - a.savedAt)
            .slice(0, 50),
        );
      request.onerror = () =>
        reject(new Error("Recovery packages could not load"));
    });
  } finally {
    db.close();
  }
}
